import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Castle,
  CircleAlert,
  CircleCheck,
  Copy,
  FileText,
  FileUp,
  Flag,
  Flame,
  type LucideIcon,
  MoveUp,
  Network,
  PencilLine,
  Plus,
  ShieldCheck,
  Shuffle,
  ShieldX,
  Sparkles,
  Trash2,
  Users,
  WandSparkles,
  MapPin,
} from "lucide-react";
import clsx from "clsx";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { ApiError, api } from "../../api/client";
import type {
  CustomCapacityLevel,
  CustomComposition,
  CustomEnemyDetail,
  CustomEnemySheet,
  CustomEnemySummary,
  CustomEntity,
  CustomManoeuvre,
  CustomScenario,
} from "../../api/types";
import { designate } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { Switch } from "../../components/Checkbox";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { EmptyState, Spinner } from "../../components/EmptyState";
import { ChoiceCard, PanelGroup } from "../../components/panel";
import { Motion, Presence } from "../../components/presence";
import { Select } from "../../components/Select";
import { Badge, Button, Field, IconButton, type IconProp, inputClass, Segmented, textareaClass } from "../../components/ui";
import { hasMarkers, markersFromReadable, readableMarkers } from "./markers";
import { ViewHeader } from "../../components/ViewHeader";
import { Waiting } from "../../components/Waiting";
import { lang, t, tServer } from "../../i18n/i18n";
import { plural } from "../../plural";
import { useUi } from "../../state/ui";
import { leaveWith, useUnsavedDraft } from "../../state/unsavedDrafts";
import { useCustomEnemyTarget } from "./customEnemyTarget";
import {
  CAPACITIES,
  CAPACITY_LEVELS,
  codeError,
  defaultSheet,
  DOCTRINE_CHOICES,
  ENTITIES,
  entityKey,
  entityName,
  INSPIRATIONS,
  MANOEUVRES,
  MAX_DESCRIPTION,
  MAX_DOCUMENT_BYTES,
  MAX_DOCUMENTS,
  MAX_STRENGTHS,
  NIVEAUX_TECHNO,
  PREPARING,
  REFINING,
  SCALES,
  STEPS,
  type StepKey,
  stepError,
  SUGGESTED_ENTITY,
  TYPES_DOMINANTS,
} from "./sheet";
import { UnitSymbol } from "../orbats/UnitSymbol";
import { onRadioGroupKeyDown, rovingTabIndex } from "../radioGroupKeys";
import { EnemyEmblem, StepHeading, StepRail } from "./StepRail";
import { ComposeProgress, composingNow } from "./ComposeProgress";
import { TrainingState } from "./TrainingState";
import {
  CAPACITY_ICONS,
  CAPACITY_TINTS,
  DOCTRINE_ICONS,
  enemySidc,
  INSPIRATION_ICONS,
  MANOEUVRE_ICONS,
  SCALE_ICONS,
  STEP_ICONS,
  TECHNO_ICONS,
} from "./visuals";

type Form = { code: string; name: string; sheet: CustomEnemySheet };
type TextEdits = Record<string, Partial<Pick<CustomScenario, "nameFR" | "nameEN" | "descriptionFR" | "descriptionEN">>>;

const NEW = "__new__";
const REFERENCE_ENEMIES = ["Titan", "Titane", "Mercure", "Tantale", "Deimos"];

function formOf(detail: CustomEnemyDetail | undefined): Form {
  if (!detail) return { code: "", name: "", sheet: defaultSheet() };
  return { code: detail.code, name: detail.name, sheet: detail.sheet };
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Vue « ENI personnalisés » (registre A135) : les ENI de la licence (quota « 2 sur 5 ») et l'assistant d'un ENI —
 *  cinq étapes de la fiche doctrinale (identité, forces, capacités, doctrine d'emploi, points forts et
 *  vulnérabilités avec les documents NP), les quatorze modes d'action à relire, puis « Valider l'ENI ». L'ENI est
 *  utilisable dès sa validation ; l'état de son modèle s'affiche en tête. Ouverte depuis le champ « Ennemi » des
 *  Données initiales, elle propose ensuite « Employer dans le dossier ». */
export function CustomEnemiesView() {
  const queryClient = useQueryClient();
  const notify = useUi((s) => s.notify);
  const setView = useUi((s) => s.setView);
  const openWelcome = useUi((s) => s.openWelcome);
  const exerciseId = useUi((s) => s.exerciseId);
  const target = useCustomEnemyTarget();
  const [origin] = useState(() => target.origin);
  const listing = useQuery({ queryKey: ["custom-enemies"], queryFn: api.customEnemies });
  const items = listing.data?.items ?? [];
  const quota = listing.data?.quota ?? { used: 0, max: 5 };
  const [selected, setSelected] = useState<string | null>(() => (target.create ? NEW : target.enemyId));
  useEffect(() => {
    if (selected === null && items.length > 0) setSelected(items[0]!.id);
  }, [selected, items]);
  const detailId = selected && selected !== NEW ? selected : null;
  const detailQuery = useQuery({
    queryKey: ["custom-enemy", detailId],
    queryFn: () => api.customEnemy(detailId!),
    enabled: Boolean(detailId),
    // Modèle de l'ENI en préparation ou en affinage : état relu toutes les quinze secondes.
    refetchInterval: (query) => (query.state.data?.training.busy ? 15_000 : false),
  });
  const detail = detailId ? detailQuery.data : undefined;
  const saved = useMemo(() => formOf(detail), [detail]);
  const [form, setForm] = useState<Form>(saved);
  const [edits, setEdits] = useState<TextEdits>({});
  const [step, setStep] = useState<StepKey>("identite");
  const loadedFor = useRef<string | null>(null);
  // Étape où reprendre une fois l'ENI enregistré puis relu (sans quoi la relecture ramènerait à la première).
  const resumeAt = useRef<StepKey | null>(null);
  // Autre ENI ouvert (ou relu après un enregistrement) : la saisie repart de ce qui est enregistré.
  useEffect(() => {
    const key = `${selected}|${detail?.version ?? 0}`;
    if (loadedFor.current === key) return;
    if (selected !== NEW && !detail) return;
    const opening = loadedFor.current?.split("|")[0] !== selected;
    loadedFor.current = key;
    setForm(selected === NEW ? formOf(undefined) : saved);
    setEdits({});
    if (opening) setStep(resumeAt.current ?? (selected === NEW ? "identite" : detail?.scenarios ? "modes" : "identite"));
    resumeAt.current = null;
  }, [selected, detail, saved]);

  const isNew = selected === NEW;
  const dirty = isNew ? form.code.trim() !== "" : !same(form, saved) || Object.keys(edits).length > 0;
  const taken = items.filter((e) => e.id !== detailId).map((e) => e.code);

  const refresh = (enemy?: CustomEnemyDetail) => {
    if (enemy) queryClient.setQueryData(["custom-enemy", enemy.id], enemy);
    void queryClient.invalidateQueries({ queryKey: ["custom-enemies"] });
  };
  const failed = (text: string) => (e: Error) =>
    notify({ text, detail: e instanceof ApiError && e.status === 412 ? t("Rechargez l'ENI : un autre poste l'a modifié.") : e.message, tone: "error" });

  const save = useMutation({
    mutationFn: async (next?: StepKey) => {
      resumeAt.current = next ?? step;
      const code = form.code.trim().toUpperCase();
      const sheet = { ...form.sheet, identite: { ...form.sheet.identite, code } };
      if (isNew) return api.createCustomEnemy(code, null, sheet);
      return api.updateCustomEnemy(detail!.id, detail!.version, {
        code,
        name: "",
        sheet,
        ...(Object.keys(edits).length ? { scenarios: edits } : {}),
      });
    },
    onSuccess: (enemy) => {
      refresh(enemy);
      loadedFor.current = null;
      setSelected(enemy.id);
      notify({ text: t("ENI enregistré"), tone: "ok" });
    },
    onError: (e: Error) => {
      resumeAt.current = null;
      failed(t("Enregistrement impossible"))(e);
    },
  });
  // Modes d'action composés par le modèle d'après la fiche enregistrée (`enemy` : celle que l'enregistrement vient
  // de rendre), en arrière-plan (registre A137) : avancement relu toutes les deux secondes et demie, chaque ME
  // affiché dès qu'il est prêt ; repli sur un modèle de manœuvre si le modèle échoue.
  const composition = detail?.composition ?? null;
  const composing = composingNow(composition);
  const generate = useMutation({
    mutationFn: (enemy?: CustomEnemyDetail) => api.composeCustomScenarios((enemy ?? detail)!.id, (enemy ?? detail)!.version),
    onSuccess: (state) => {
      if (state.enemy) refresh(state.enemy);
    },
    onError: failed(t("Composition impossible")),
  });
  const recompose = useMutation({
    mutationFn: (key: string) => api.recomposeCustomScenario(detail!.id, key, detail!.version),
    onSuccess: (state) => {
      if (state.enemy) refresh(state.enemy);
    },
    onError: failed(t("Nouvelle proposition impossible")),
  });
  const progress = useQuery({
    queryKey: ["custom-enemy-composition", detailId],
    queryFn: () => api.customComposition(detailId!, detail?.version),
    enabled: Boolean(detailId) && composing,
    refetchInterval: composing ? 2_500 : false,
  });
  useEffect(() => {
    const data = progress.data;
    if (!data) return;
    if (data.enemy) queryClient.setQueryData(["custom-enemy", data.enemy.id], data.enemy);
    // ENI inchangé (composition arrêtée sur une erreur) : seul l'état de la composition est repris.
    else queryClient.setQueryData<CustomEnemyDetail>(["custom-enemy", data.id], (old) => (old ? { ...old, composition: data.composition } : old));
  }, [progress.data, queryClient]);
  // Fin de la composition : dite une fois (ME composés à partir d'un modèle de manœuvre comptés).
  const wasComposing = useRef<string | null>(null);
  useEffect(() => {
    const key = detailId && composing ? `${detailId}|${composition?.mode}` : null;
    const previous = wasComposing.current;
    wasComposing.current = key;
    if (!previous || key || !composition || previous.split("|")[0] !== detailId) return;
    if (composition.state !== "done") {
      notify({ text: t("Composition arrêtée"), detail: composition.error ? tServer(composition.error) : undefined, tone: "warn" });
      return;
    }
    const one = composition.mode === "one";
    notify({
      text: one ? t("Nouveau mode d'action proposé") : t("Modes d'action composés"),
      detail: composition.fallbacks
        ? t("{n} composés à partir d'un modèle de manœuvre. Relisez-les avant de valider l'ENI.", { n: composition.fallbacks })
        : one
          ? t("Relisez-le avant de valider l'ENI.")
          : t("Relisez-les avant de valider l'ENI."),
      tone: "ok",
    });
  }, [composing, composition, detailId, notify]);
  const validate = useMutation({
    mutationFn: () => api.validateCustomEnemy(detail!.id, detail!.version),
    onSuccess: (enemy) => {
      refresh(enemy);
      notify({ text: t("ENI validé"), detail: t("Il est utilisable dès maintenant dans vos dossiers."), tone: "ok" });
    },
    onError: failed(t("Validation impossible")),
  });
  const duplicate = useMutation({
    mutationFn: () => api.duplicateCustomEnemy(detail!.id),
    onSuccess: (enemy) => {
      refresh(enemy);
      setSelected(enemy.id);
      notify({ text: t("Copie créée"), tone: "ok" });
    },
    onError: failed(t("Duplication impossible")),
  });
  const [deleting, setDeleting] = useState<{ dossiers: { id: string; name: string }[] } | null>(null);
  const remove = useMutation({
    mutationFn: (force: boolean) => api.deleteCustomEnemy(detail!.id, force),
    onSuccess: (result) => {
      setDeleting(null);
      setSelected(null);
      void queryClient.invalidateQueries({ queryKey: ["custom-enemies"] });
      void queryClient.invalidateQueries({ queryKey: ["exercises"] });
      // Textes des dossiers qui le décrivaient remis à zéro (mise en ambiance, fiche pays, ordres) : le dire.
      const reset = result?.reset;
      if (reset && reset.dossiers > 0) {
        void queryClient.invalidateQueries();
        notify({
          text: t("ENI supprimé · {texts} remis à zéro dans {dossiers}", {
            texts: plural(reset.sections, t("texte"), t("textes")),
            dossiers: plural(reset.dossiers, t("dossier"), t("dossiers")),
          }),
          tone: "ok",
        });
      } else notify({ text: t("ENI supprimé"), tone: "ok" });
    },
    onError: (e: Error) => {
      const dossiers = e instanceof ApiError ? (e.detail?.dossiers as { id: string; name: string }[] | undefined) : undefined;
      if (dossiers) setDeleting({ dossiers });
      else failed(t("Suppression impossible"))(e);
    },
  });

  const drop = useUnsavedDraft(dirty ? `custom-enemy:${selected}` : null, dirty, {
    label: t("ENI personnalisé « {code} »", { code: form.code || "…" }),
    save: () => save.mutateAsync(),
    discard: () => {
      setForm(saved);
      setEdits({});
    },
  });
  const choose = (id: string) => void leaveWith(() => {
    drop();
    setSelected(id);
  });
  const close = () => void leaveWith(() => (exerciseId ? setView("mission") : openWelcome()));
  const employ = () => {
    if (!detail) return;
    useCustomEnemyTarget.getState().employ({ exerciseId: origin?.exerciseId ?? exerciseId, enemyId: detail.id, baseEnemy: detail.base_enemy });
    setView("mission");
  };

  const index = STEPS.findIndex((s) => s.key === step);
  // Ce qui manque à l'étape : dit à côté du champ fautif ; l'action principale, bloquée, le désigne.
  const blocking = stepError(step, form.sheet, form.code, taken, detail?.code ?? null);
  const [insist, setInsist] = useState(false);
  useEffect(() => setInsist(false), [step, selected]);
  const showFault = () => {
    setInsist(true);
    designate(`[data-eni-field="${faultField(step, form, detail?.code ?? null)}"]`, "field");
  };
  const go = (next: number) => {
    const key = STEPS[next]?.key;
    if (key) setStep(key);
  };
  const hasScenarios = Object.keys(detail?.scenarios?.scenarios ?? {}).length > 0;
  const compose = () => {
    if (!dirty) return generate.mutate(undefined);
    save.mutateAsync(step).then((enemy) => generate.mutate(enemy), () => undefined);
  };
  const last = index === STEPS.length - 1;
  // Action principale unique de l'étape : enregistrer le nouvel ENI, passer à l'étape suivante, enregistrer les
  // retouches, valider l'ENI, puis l'employer dans le dossier d'origine. Composer les modes d'action : bouton de
  // l'étape elle-même.
  let primary: ReactNode = null;
  if (isNew) {
    primary = (
      <Button onClick={() => save.mutate(STEPS[1].key)} blocked={Boolean(blocking)} onBlocked={showFault} disabled={save.isPending}>
        {save.isPending ? <Waiting>{t("Enregistrement…")}</Waiting> : t("Enregistrer et continuer")} <ArrowRight size={14} />
      </Button>
    );
  } else if (!last) {
    primary = (
      <Button onClick={() => go(index + 1)} blocked={Boolean(blocking)} onBlocked={showFault}>
        {t("Étape suivante")} <ArrowRight size={14} />
      </Button>
    );
  } else if (hasScenarios && dirty) {
    primary = (
      <Button onClick={() => save.mutate(undefined)} disabled={save.isPending}>
        {save.isPending ? <Waiting>{t("Enregistrement…")}</Waiting> : t("Enregistrer")}
      </Button>
    );
  } else if (hasScenarios && detail?.status === "draft") {
    primary = (
      <Button onClick={() => validate.mutate()} disabled={validate.isPending || composing}>
        <CircleCheck size={14} /> {validate.isPending ? <Waiting>{t("Validation…")}</Waiting> : t("Valider l'ENI")}
      </Button>
    );
  } else if (detail?.status === "ready" && origin && !dirty) {
    primary = <Button onClick={employ}>{t("Employer dans le dossier")}</Button>;
  }
  const secondarySave = dirty && !isNew && !last;
  const full = quota.used >= quota.max;
  const newEnemy = () => {
    if (full) {
      notify({ text: t("Nombre maximal d'ENI personnalisés atteint"), detail: t("Supprimez-en un pour en créer un autre."), tone: "warn" });
      return;
    }
    void leaveWith(() => setSelected(NEW));
  };

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-canvas">
      <ViewHeader
        view="enemies"
        context={origin?.exerciseName ?? undefined}
        badges={<Badge title={t("ENI personnalisés de votre licence")}>{t("{used} sur {max}", quota)}</Badge>}
        actions={
          <Button size="sm" onClick={newEnemy} blocked={full} onBlocked={newEnemy}>
            <Plus size={14} /> {t("Nouvel ENI")}
          </Button>
        }
        onClose={close}
      />
      <div className="flex min-h-0 flex-1 max-md:flex-col">
        <aside className="w-72 shrink-0 overflow-y-auto border-r border-line bg-surface max-md:max-h-56 max-md:w-full max-md:border-r-0 max-md:border-b">
          <PanelGroup title={t("ENI de votre licence")}>
            <div className="space-y-1.5 px-2 pb-2">
              {items.map((e) => (
                <ChoiceCard
                  key={e.id}
                  selected={e.id === selected}
                  onClick={() => choose(e.id)}
                  icon={<EnemyEmblem type={e.id === detailId && detail ? form.sheet.forces.type_dominant : e.type_dominant} size="sm" />}
                  title={t("ENI {code}", { code: e.code })}
                  detail={<ListStatus enemy={e} />}
                />
              ))}
              <Presence show={isNew}>
                <Motion origin="fade">
                  <ChoiceCard
                    selected
                    icon={<EnemyEmblem type={form.sheet.forces.type_dominant} size="sm" />}
                    title={form.code ? t("ENI {code}", { code: form.code.toUpperCase() }) : t("Nouvel ENI")}
                    detail={
                      <span className="flex items-center gap-1">
                        <PencilLine size={12} className="shrink-0" aria-hidden /> {t("Non enregistré")}
                      </span>
                    }
                  />
                </Motion>
              </Presence>
              {items.length === 0 && selected === null && (
                <p className="px-2 py-1.5 text-meta leading-snug text-ink-muted">
                  {t("Aucun ENI personnalisé : créez le vôtre d'après votre propre doctrine.")}
                </p>
              )}
            </div>
          </PanelGroup>
        </aside>
        <main className="min-h-0 min-w-0 flex-1 overflow-y-auto">
          {selected === null ? (
            <Motion origin="fade" className="h-full">
              <EmptyState
                icon={<EmptyEmblem />}
                title={t("Créez un ENI personnalisé")}
                action={
                  <Button onClick={newEnemy} blocked={full} onBlocked={newEnemy}>
                    <Plus size={14} /> {t("Nouvel ENI")}
                  </Button>
                }
                note={t("{used} sur {max} ENI personnalisés employés par votre licence.", quota)}
              >
                {t("Décrivez sa doctrine en cinq étapes : Generex en tire ses modes d'action, que vous relisez avant de le valider.")}
                <StepsPreview />
              </EmptyState>
            </Motion>
          ) : !isNew && !detail ? (
            <EmptyState variant="loading" title={t("Chargement de l'ENI…")} />
          ) : (
            <Motion key={selected} origin="fade" className="mx-auto max-w-3xl space-y-4 px-4 py-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <Motion key={form.sheet.forces.type_dominant} origin="fade">
                    <EnemyEmblem type={form.sheet.forces.type_dominant} size="lg" />
                  </Motion>
                  <div className="min-w-0">
                    <h2 className="text-heading leading-snug font-semibold break-words text-ink">
                      {form.code ? t("ENI {code}", { code: form.code.toUpperCase() }) : t("Nouvel ENI")}
                    </h2>
                    <SheetBadges sheet={form.sheet} />
                    {detail && <TrainingState enemy={detail} />}
                  </div>
                </div>
                {detail && (
                  <div className="flex items-center gap-1">
                    <IconButton
                      size="sm"
                      onClick={() => duplicate.mutate()}
                      disabled={duplicate.isPending}
                      title={t("Dupliquer")}
                      aria-label={t("Dupliquer")}
                    >
                      <Copy size={14} />
                    </IconButton>
                    <IconButton
                      size="sm"
                      variant="danger"
                      onClick={() => setDeleting({ dossiers: [] })}
                      title={t("Supprimer")}
                      aria-label={t("Supprimer")}
                    >
                      <Trash2 size={14} />
                    </IconButton>
                  </div>
                )}
              </div>
              <StepRail value={step} onChange={setStep} locked={(key) => isNew && key !== "identite"} />
              <Motion key={step} origin="fade" className="space-y-4">
                <StepHeading step={step} />
                {step === "identite" && <IdentityStep form={form} setForm={setForm} taken={taken} saved={detail?.code ?? null} insist={insist} />}
                {step === "forces" && <ForcesStep form={form} setForm={setForm} />}
                {step === "capacites" && <CapacitiesStep form={form} setForm={setForm} />}
                {step === "doctrine" && <DoctrineStep form={form} setForm={setForm} />}
                {step === "forces_faiblesses" && <StrengthsStep form={form} setForm={setForm} enemy={detail} onChange={refresh} />}
                {step === "modes" && detail && (
                  <ModesStep
                    enemy={detail}
                    edits={edits}
                    setEdits={setEdits}
                    dirty={dirty}
                    generating={generate.isPending || (save.isPending && step === "modes")}
                    onGenerate={compose}
                    composition={composition}
                    composing={composing}
                    recomposing={recompose.isPending ? recompose.variables : null}
                    onRecompose={(key) => recompose.mutate(key)}
                  />
                )}
              </Motion>
              {step === "modes" && detail && hasScenarios && !composing && <NextSteps enemy={detail} fromDossier={Boolean(origin)} />}
              <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
                <Button variant="secondary" onClick={() => go(index - 1)} disabled={index === 0}>
                  <ArrowLeft size={14} /> {t("Précédent")}
                </Button>
                <span className="flex-1" />
                {secondarySave && (
                  <Button variant="secondary" onClick={() => save.mutate(undefined)} blocked={Boolean(blocking)} onBlocked={showFault} disabled={save.isPending}>
                    {save.isPending ? <Waiting>{t("Enregistrement…")}</Waiting> : t("Enregistrer")}
                  </Button>
                )}
                {primary}
              </div>
            </Motion>
          )}
        </main>
      </div>
      <Presence show={deleting !== null && Boolean(detail)}>
        {deleting && detail && (
          <ConfirmDialog
            title={t("Supprimer l'ENI {code} ?", { code: detail.code })}
            confirmLabel={deleting.dossiers.length ? t("Supprimer quand même") : t("Supprimer")}
            busy={remove.isPending}
            onConfirm={() => remove.mutate(deleting.dossiers.length > 0)}
            onCancel={() => setDeleting(null)}
          >
            {deleting.dossiers.length ? (
              <>
                <p>{t("Ces dossiers l'affrontent ; ils reprendront l'ennemi de référence {base} :", { base: detail.base_enemy })}</p>
                <ul className="mt-1.5 list-disc pl-5">
                  {deleting.dossiers.map((d) => (
                    <li key={d.id}>{d.name}</li>
                  ))}
                </ul>
                <p className="mt-2">{t("Les textes de ces dossiers qui décrivaient cet ENI seront remis à zéro.")}</p>
                <p className="mt-2">{t("Sa fiche, ses modes d'action, ses documents, son modèle d'IA et ses RETEX seront supprimés.")}</p>
              </>
            ) : (
              <p>{t("Sa fiche, ses modes d'action, ses documents, son modèle d'IA et ses RETEX seront supprimés.")}</p>
            )}
          </ConfirmDialog>
        )}
      </Presence>
    </div>
  );
}

// --- Étapes de la fiche -------------------------------------------------------------------------------------------

type StepProps = { form: Form; setForm: (update: (f: Form) => Form) => void };

function setSheet<K extends keyof CustomEnemySheet>(
  setForm: StepProps["setForm"],
  part: K,
  changes: Partial<CustomEnemySheet[K]>,
) {
  setForm((f) => ({ ...f, sheet: { ...f.sheet, [part]: { ...f.sheet[part], ...changes } } }));
}

function Counter({ value, max }: { value: string; max: number }) {
  return (
    <span className={value.length > max ? "text-danger" : undefined}>{t("{n} / {max} caractères", { n: value.length, max })}</span>
  );
}

/** Champ à désigner quand l'étape est incomplète (attribut `data-eni-field`). */
function faultField(step: StepKey, form: Form, saved: string | null): string {
  if (step === "identite") return form.sheet.identite.description.length > MAX_DESCRIPTION && codeError(form.code, [], saved) === null ? "description" : "code";
  if (step === "doctrine") return "manoeuvres";
  if (step === "forces_faiblesses") return form.sheet.forces_faiblesses.points_forts.length > MAX_STRENGTHS ? "points_forts" : "vulnerabilites";
  return "";
}

/** Ce qui se passe après la validation, et comment employer l'ENI dans un dossier. */
function NextSteps({ enemy, fromDossier }: { enemy: CustomEnemyDetail; fromDossier: boolean }) {
  const ready = enemy.status === "ready";
  return (
    <Callout tone={ready ? "ok" : "info"} report={false} title={ready ? t("ENI validé : il est utilisable dès maintenant") : t("Après la validation")}>
      <ul className="mt-0.5 list-disc space-y-0.5 pl-4">
        {!ready && <li>{t("L'ENI est utilisable aussitôt dans vos dossiers.")}</li>}
        <li>
          {enemy.training.enabled === false
            ? t("Ses textes sont rédigés par le modèle d'IA habituel, d'après sa fiche et ses modes d'action.")
            : t("Son modèle se prépare en quelques minutes, puis s'affine quand le serveur est disponible ; son état s'affiche sous son nom.")}
        </li>
        <li>
          {fromDossier
            ? t("Pour l'affronter : « Employer dans le dossier », ou dans un autre dossier, Données initiales, champ « Ennemi ».")
            : t("Pour l'affronter : ouvrez un dossier, puis dans les Données initiales choisissez-le dans le champ « Ennemi ».")}
        </li>
      </ul>
    </Callout>
  );
}

// --- Pictogrammes de la liste et de l'en-tête ---------------------------------------------------------------------

/** État d'un ENI sur sa carte de la liste : brouillon, modèle en préparation ou en affinage (roue violette, tâche
 *  en cours), prêt (coche verte) ou en échec (alerte rouge). */
function ListStatus({ enemy }: { enemy: CustomEnemySummary }) {
  const status = enemy.training.status;
  const draft = enemy.status === "draft";
  const icon = draft ? (
    <PencilLine size={12} aria-hidden />
  ) : status === "failed" ? (
    <CircleAlert size={12} className="text-danger" aria-hidden />
  ) : PREPARING.includes(status) || REFINING.includes(status) ? (
    <Spinner size="sm" tone="run" decorative className="size-3" />
  ) : status === "ready" || status === "quick_ready" ? (
    <CircleCheck size={12} className="text-ok" aria-hidden />
  ) : null;
  return (
    <span className="flex items-start gap-1">
      {icon && <span className="mt-px flex shrink-0">{icon}</span>}
      <span className="min-w-0">{draft ? t("Brouillon : à valider") : <Waiting>{t(enemy.training.label)}</Waiting>}</span>
    </span>
  );
}

/** Ce que dit la fiche en trois mots, sous le nom de l'ENI : inspiration, type de forces, niveau technologique. */
function SheetBadges({ sheet }: { sheet: CustomEnemySheet }) {
  const labels = [
    INSPIRATIONS.find((o) => o.value === sheet.identite.inspiration)?.label,
    TYPES_DOMINANTS.find((o) => o.value === sheet.forces.type_dominant)?.label,
    NIVEAUX_TECHNO.find((o) => o.value === sheet.forces.niveau_techno)?.label,
  ].filter((label): label is string => Boolean(label));
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {labels.map((label) => (
        <Badge key={label}>{t(label)}</Badge>
      ))}
    </div>
  );
}

/** Illustration de l'état vide : trois symboles APP-6 ennemis (chars, interarmes, infanterie légère). */
function EmptyEmblem() {
  return (
    <span className="flex items-end gap-2" aria-hidden>
      <EnemyEmblem type="blinde_mecanise" size="md" />
      <EnemyEmblem type="hybride" size="lg" />
      <EnemyEmblem type="infanterie_legere" size="md" />
    </span>
  );
}

/** Les cinq étapes de la fiche, illustrées, dans l'état vide. */
function StepsPreview() {
  return (
    <ol className="mt-5 grid grid-cols-5 gap-2 max-sm:grid-cols-1 max-sm:gap-1.5">
      {STEPS.slice(0, 5).map((s, i) => {
        const Icon = STEP_ICONS[s.key];
        return (
          <li key={s.key} className="flex flex-col items-center gap-1.5 text-center max-sm:flex-row max-sm:text-left">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-line-strong bg-surface text-ink-muted" aria-hidden>
              <Icon size={15} />
            </span>
            <span className="text-meta leading-tight text-ink-soft">
              <span className="text-ink-muted tabular-nums">{i + 1}.</span> {t(s.label)}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

// --- Cartes de choix illustrées -----------------------------------------------------------------------------------

const COLUMNS = {
  2: "grid-cols-2 max-sm:grid-cols-1",
  3: "grid-cols-3 max-md:grid-cols-2 max-sm:grid-cols-1",
} as const;

/** Choix unique en cartes illustrées (groupe radio, flèches au clavier) : icône, libellé, précision facultative. */
function ChoiceGrid<T extends string>({
  label,
  options,
  value,
  onChange,
  icon,
  columns = 3,
  bare = false,
}: {
  label: string;
  /** Libellé lu seulement (le titre du bloc le dit déjà). */
  bare?: boolean;
  options: readonly { value: T; label: string; detail?: string }[];
  value: T;
  onChange: (value: T) => void;
  icon?: (value: T) => IconProp;
  columns?: keyof typeof COLUMNS;
}) {
  const selected = options.findIndex((o) => o.value === value);
  const enabled = options.map(() => true);
  return (
    <div>
      {!bare && <p className="mb-1.5 text-ui font-medium text-ink-soft">{label}</p>}
      <div
        role="radiogroup"
        aria-label={label}
        className={clsx("grid auto-rows-fr gap-1.5", COLUMNS[columns])}
        onKeyDown={(e) => onRadioGroupKeyDown(e, (i) => onChange(options[i]!.value))}
      >
        {options.map((o, i) => (
          <ChoiceCard
            key={o.value}
            radio
            className="h-full"
            selected={o.value === value}
            tabIndex={rovingTabIndex(i, selected, enabled)}
            onClick={() => onChange(o.value)}
            icon={icon?.(o.value)}
            title={t(o.label)}
            detail={o.detail ? t(o.detail) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

/** Bloc thématique d'une étape (doctrine) : pictogramme et titre, puis ses réglages. */
function StepBlock({ icon: Icon, title, children }: { icon: LucideIcon; title: string; children: ReactNode }) {
  return (
    <section className="space-y-3 rounded-lg border border-line bg-surface px-3 py-3">
      <h4 className="flex items-center gap-2 text-ui font-semibold text-ink">
        <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-canvas text-ink-muted" aria-hidden>
          <Icon size={14} />
        </span>
        {title}
      </h4>
      {children}
    </section>
  );
}

// --- Étapes de la fiche -------------------------------------------------------------------------------------------

function IdentityStep({ form, setForm, taken, saved, insist }: StepProps & { taken: string[]; saved: string | null; insist: boolean }) {
  // Erreur du nom de code dite sous le champ dès la saisie, ou quand l'action principale la rencontre.
  const error = form.code || insist ? codeError(form.code, taken, saved) : null;
  return (
    <>
      <div className="max-w-md">
        <Field label={t("Nom de l'ENI (obligatoire)")}>
          <input
            className={inputClass}
            data-eni-field="code"
            aria-invalid={error ? true : undefined}
            value={form.code}
            maxLength={24}
            autoFocus={!form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase().replace(/\s+/g, "-") }))}
          />
          <span className={clsx("mt-1 flex items-start gap-1 text-meta leading-snug", error ? "text-danger" : "text-ink-muted")}>
            {error && <CircleAlert size={12} className="mt-px shrink-0" aria-hidden />}
            {error ?? t("Le nom affiché partout, en majuscules (TITAN, MERCURE… sont réservés).")}
          </span>
        </Field>
      </div>
      <ChoiceGrid
        label={t("Inspiration")}
        columns={2}
        options={INSPIRATIONS}
        value={form.sheet.identite.inspiration}
        onChange={(inspiration) => setSheet(setForm, "identite", { inspiration })}
        icon={(value) => INSPIRATION_ICONS[value]}
      />
      <EntityField form={form} setForm={setForm} />
      <Field label={t("Description")}>
        <textarea
          className={textareaClass}
          data-eni-field="description"
          rows={4}
          value={form.sheet.identite.description}
          onChange={(e) => setSheet(setForm, "identite", { description: e.target.value })}
          placeholder={t("Origine, posture générale, objectifs de cet ennemi.")}
        />
        <span className="mt-1 block text-meta text-ink-muted">
          <Counter value={form.sheet.identite.description} max={MAX_DESCRIPTION} />
        </span>
      </Field>
    </>
  );
}

/** Formule de l'entité (« ALLIANCE BOREAL », « FRONT BOREAL ») : proposée d'après l'inspiration et modifiable. Tant
 *  que l'utilisateur n'en a pas choisi une lui-même, la proposition suit l'inspiration ; « Reprendre la proposition »
 *  y revient. Aperçu en direct du nom formé dans les deux langues des documents. */
function EntityField({ form, setForm }: StepProps) {
  const identite = form.sheet.identite;
  const suggested = SUGGESTED_ENTITY[identite.inspiration];
  const current = entityKey(identite);
  const chosen = Boolean(identite.entite);
  const enabled = ENTITIES.map(() => true);
  const selected = ENTITIES.findIndex((e) => e.value === current);
  // La proposition choisie revient à la suivre : elle changera avec l'inspiration.
  const choose = (value: CustomEntity) => setSheet(setForm, "identite", { entite: value === suggested ? "" : value });
  return (
    <div>
      <div className="mb-1.5 flex min-h-5 flex-wrap items-center gap-x-3 gap-y-1">
        <p className="text-ui font-medium text-ink-soft">{t("Formule de l'entité")}</p>
        <span className="flex-1" />
        <Presence show={chosen}>
          <Motion origin="fade">
            <Button variant="link" onClick={() => setSheet(setForm, "identite", { entite: "" })}>
              {t("Reprendre la proposition ({word})", { word: t(ENTITIES.find((e) => e.value === suggested)!.label) })}
            </Button>
          </Motion>
        </Presence>
      </div>
      <div
        role="radiogroup"
        aria-label={t("Formule de l'entité")}
        className="grid auto-rows-fr grid-cols-5 gap-1.5 max-md:grid-cols-3 max-sm:grid-cols-2"
        onKeyDown={(e) => onRadioGroupKeyDown(e, (i) => choose(ENTITIES[i]!.value))}
      >
        {ENTITIES.map((e, i) => (
          <ChoiceCard
            key={e.value}
            radio
            className="h-full"
            selected={e.value === current}
            tabIndex={rovingTabIndex(i, selected, enabled)}
            onClick={() => choose(e.value)}
            title={t(e.label)}
            detail={e.value === suggested ? t("Proposée") : undefined}
          />
        ))}
      </div>
      <div className="mt-2 flex items-start gap-3 rounded-lg border border-line bg-canvas px-3 py-2.5" data-testid="custom-enemy-entity-preview">
        <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-hostile-soft text-hostile" aria-hidden>
          <Flag size={14} />
        </span>
        <dl className="grid min-w-0 flex-1 grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-1">
          <dt className="text-meta text-ink-muted">{t("Dans les documents en français")}</dt>
          <dd className="font-semibold break-words text-ink">
            <Motion key={`fr-${entityName(identite, form.code, "fr")}`} origin="fade" className="text-ui">
              {entityName(identite, form.code, "fr")}
            </Motion>
          </dd>
          <dt className="text-meta text-ink-muted">{t("Dans les documents en anglais")}</dt>
          <dd className="font-semibold break-words text-ink">
            <Motion key={`en-${entityName(identite, form.code, "en")}`} origin="fade" className="text-ui">
              {entityName(identite, form.code, "en")}
            </Motion>
          </dd>
        </dl>
      </div>
    </div>
  );
}

function ForcesStep({ form, setForm }: StepProps) {
  const odbs = useQuery({ queryKey: ["odbs", "ENI"], queryFn: () => api.odbs("ENI") });
  const forces = form.sheet.forces;
  return (
    <>
      <Field
        label={t("Ordre de bataille de référence")}
        hint={t("Ses unités et sa faction portent l'ENI sur la carte ; générique : d'après le type de forces dominant.")}
      >
        <Select className={inputClass} value={forces.odb_reference} onChange={(e) => setSheet(setForm, "forces", { odb_reference: e.target.value })}>
          <option value="">{t("Générique (d'après le type de forces)")}</option>
          <optgroup label={t("Ennemis de référence")}>
            {REFERENCE_ENEMIES.map((name) => (
              <option key={name} value={name}>
                {t("Ennemi de référence ({enemy})", { enemy: name })}
              </option>
            ))}
          </optgroup>
          {(odbs.data?.user.length ?? 0) > 0 && (
            <optgroup label={t("Vos ordres de bataille ennemis")}>
              {odbs.data!.user.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.name}
                </option>
              ))}
            </optgroup>
          )}
        </Select>
      </Field>
      <ChoiceGrid
        label={t("Type de forces dominant")}
        options={TYPES_DOMINANTS}
        value={forces.type_dominant}
        onChange={(type_dominant) => setSheet(setForm, "forces", { type_dominant })}
        icon={(value) => <UnitSymbol sidc={enemySidc(value)} size={13} className="w-6" />}
      />
      <Segmented
        label={t("Niveau technologique")}
        options={NIVEAUX_TECHNO.map((o) => ({ value: o.value, label: t(o.label), icon: TECHNO_ICONS[o.value] }))}
        value={forces.niveau_techno}
        onChange={(value) => setSheet(setForm, "forces", { niveau_techno: value })}
      />
    </>
  );
}

/** Étape « Capacités » : une ligne par capacité (pictogramme teinté selon le niveau, quatre niveaux). Exportée pour
 *  les essais. */
export function CapacitiesStep({ form, setForm }: StepProps) {
  const levels = CAPACITY_LEVELS.map((o) => ({ value: o.value, label: t(o.label) }));
  return (
    <div className="divide-y divide-line rounded-lg border border-line bg-surface">
      {CAPACITIES.map((c) => {
        const Icon = CAPACITY_ICONS[c.value];
        // Fiche enregistrée avant une capacité (la logistique, 30/10/2026) : niveau moyen, comme le serveur.
        const level = form.sheet.capacites[c.value] ?? "moyen";
        return (
          <div key={c.value} className="flex min-h-8 items-center gap-3 px-3 py-1.5 max-sm:flex-wrap">
            <span
              className={clsx("flex size-7 shrink-0 items-center justify-center rounded-md border transition-colors", CAPACITY_TINTS[level])}
              aria-hidden
            >
              <Icon size={15} />
            </span>
            <span className="min-w-0 flex-1 text-ui text-ink">{t(c.label)}</span>
            <Segmented<CustomCapacityLevel>
              ariaLabel={t(c.label)}
              size="sm"
              slide
              className="w-72 max-sm:w-full"
              options={levels}
              value={level}
              onChange={(value) => setSheet(setForm, "capacites", { [c.value]: value } as Partial<CustomEnemySheet["capacites"]>)}
            />
          </div>
        );
      })}
    </div>
  );
}

type DoctrineKey = keyof typeof DOCTRINE_CHOICES;

function DoctrineStep({ form, setForm }: StepProps) {
  const doctrine = form.sheet.doctrine;
  const moves = doctrine.manoeuvre_preferee;
  const toggleMove = (value: CustomManoeuvre) =>
    setSheet(setForm, "doctrine", { manoeuvre_preferee: moves.includes(value) ? moves.filter((m) => m !== value) : [...moves, value] });
  const choice = (key: DoctrineKey, columns: 2 | 3 = 3, bare = false) => {
    const spec = DOCTRINE_CHOICES[key];
    return (
      <ChoiceGrid<string>
        label={t(spec.label)}
        columns={columns}
        bare={bare}
        options={spec.options}
        value={doctrine[key]}
        onChange={(value) => setSheet(setForm, "doctrine", { [key]: value } as Partial<CustomEnemySheet["doctrine"]>)}
        icon={(value) => DOCTRINE_ICONS[key]?.[value]}
      />
    );
  };
  const scale = (key: (typeof SCALES)[number]["key"]) => {
    const spec = SCALES.find((s) => s.key === key)!;
    const { low: Low, high: High } = SCALE_ICONS[key];
    return (
      <div>
        <Segmented<string>
          label={t(spec.label)}
          slide
          options={[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: String(n) }))}
          value={String(doctrine[key])}
          onChange={(value) => setSheet(setForm, "doctrine", { [key]: Number(value) } as Partial<CustomEnemySheet["doctrine"]>)}
        />
        <p className="mt-1 flex justify-between gap-2 text-meta text-ink-muted">
          <span className="flex items-center gap-1">
            <Low size={12} className="shrink-0" aria-hidden /> {t(spec.low)}
          </span>
          <span className="flex items-center gap-1">
            {t(spec.high)} <High size={12} className="shrink-0" aria-hidden />
          </span>
        </p>
      </div>
    );
  };
  return (
    <>
      <StepBlock icon={Network} title={t("Commandement et articulation")}>
        {choice("commandement", 2)}
        {choice("echelonnement")}
      </StepBlock>
      <StepBlock icon={MoveUp} title={t("Manœuvre")}>
        <div>
          <p className="mb-1.5 text-ui font-medium text-ink-soft">{t("Manœuvre préférée (obligatoire : une ou deux)")}</p>
          <div className="grid grid-cols-2 gap-1.5 rounded-lg max-sm:grid-cols-1" data-eni-field="manoeuvres">
            {MANOEUVRES.map((o) => {
              const on = moves.includes(o.value);
              return (
                <ChoiceCard
                  key={o.value}
                  selected={on}
                  icon={MANOEUVRE_ICONS[o.value]}
                  title={t(o.label)}
                  blocked={!on && moves.length >= 2}
                  onBlocked={() => undefined}
                  onClick={() => toggleMove(o.value)}
                />
              );
            })}
          </div>
          <Presence show={moves.length === 0}>
            <Motion origin="fade" className="mt-1 flex items-center gap-1 text-meta text-danger">
              <CircleAlert size={12} className="shrink-0" aria-hidden /> {t("Choisissez une ou deux manœuvres préférées.")}
            </Motion>
          </Presence>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 max-sm:grid-cols-1">
          {scale("tempo")}
          {scale("choc_usure")}
        </div>
        <Switch
          checked={doctrine.profondeur}
          onChange={(next) => setSheet(setForm, "doctrine", { profondeur: next })}
          label={t("Actions dans la profondeur (raids, forces spéciales, frappes lointaines)")}
        />
      </StepBlock>
      <StepBlock icon={Flame} title={t("Feux et obstacles")}>
        {choice("feux", 2)}
        <div className="sm:max-w-[50%] sm:pr-2">{scale("obstacles")}</div>
      </StepBlock>
      <StepBlock icon={Castle} title={t("Défense et contre-attaque")}>
        {choice("defense")}
        {choice("contre_attaque")}
        <Segmented<string>
          label={t(DOCTRINE_CHOICES.pertes.label)}
          slide
          options={DOCTRINE_CHOICES.pertes.options.map((o) => ({ value: o.value, label: t(o.label) }))}
          value={doctrine.pertes}
          onChange={(value) => setSheet(setForm, "doctrine", { pertes: value } as Partial<CustomEnemySheet["doctrine"]>)}
        />
      </StepBlock>
      <StepBlock icon={Users} title={t("Population")}>
        {choice("population", 3, true)}
      </StepBlock>
    </>
  );
}

function StrengthsStep({
  form,
  setForm,
  enemy,
  onChange,
}: StepProps & { enemy: CustomEnemyDetail | undefined; onChange: (enemy: CustomEnemyDetail) => void }) {
  const notify = useUi((s) => s.notify);
  const input = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: (file: File) => api.addCustomEnemyDocument(enemy!.id, file),
    onSuccess: ({ notice, ...detail }) => {
      onChange(detail);
      // Texte au-delà de 300 000 signes : l'avis du serveur dit ce qui est gardé.
      if (notice) notify({ text: t("Document ajouté"), detail: tServer(notice), tone: "warn" });
      else notify({ text: t("Document ajouté"), detail: t("Seul son texte est gardé."), tone: "ok" });
    },
    onError: (e: Error) => notify({ text: t("Document refusé"), detail: e.message, tone: "error" }),
  });
  const removeDoc = useMutation({
    mutationFn: (id: string) => api.removeCustomEnemyDocument(enemy!.id, id),
    onSuccess: onChange,
    onError: (e: Error) => notify({ text: t("Retrait impossible"), detail: e.message, tone: "error" }),
  });
  const ff = form.sheet.forces_faiblesses;
  const docs = enemy?.documents ?? [];
  const pick = (file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_DOCUMENT_BYTES) {
      notify({ text: t("Document trop volumineux"), detail: t("10 Mo au plus."), tone: "error" });
      return;
    }
    upload.mutate(file);
  };
  const columns = [
    { key: "points_forts", label: t("Points forts"), icon: <ShieldCheck size={14} className="shrink-0 text-ok" aria-hidden /> },
    { key: "vulnerabilites", label: t("Vulnérabilités"), icon: <ShieldX size={14} className="shrink-0 text-warn" aria-hidden /> },
  ] as const;
  return (
    <>
      <div className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
        {columns.map(({ key, label, icon }) => (
          <label key={key} className="block">
            <span className="mb-1 flex items-center gap-1.5 text-ui font-medium text-ink-soft">
              {icon} {label}
            </span>
            <textarea
              className={textareaClass}
              data-eni-field={key}
              rows={6}
              value={ff[key]}
              onChange={(e) => setSheet(setForm, "forces_faiblesses", { [key]: e.target.value })}
            />
            <span className="mt-1 block text-meta text-ink-muted">
              <Counter value={ff[key]} max={MAX_STRENGTHS} />
            </span>
          </label>
        ))}
      </div>
      <PanelGroup
        title={t("Documents de doctrine")}
        count={docs.length}
        className="rounded-lg border border-line bg-surface"
        actions={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => input.current?.click()}
            disabled={!enemy || upload.isPending || docs.length >= MAX_DOCUMENTS}
          >
            <FileUp size={14} /> {upload.isPending ? <Waiting>{t("Lecture du document…")}</Waiting> : t("Déposer un document")}
          </Button>
        }
      >
        <div className="space-y-2 px-2 pb-2">
          <Callout tone="warn" report={false}>
            {t("Documents non protégés (NP) seulement.")} {t("PDF, Word, LibreOffice, RTF ou texte ; 10 Mo et 5 documents au plus. Seul leur texte est gardé, 300 000 signes par document au plus.")}
          </Callout>
          {docs.map((d) => (
            <Motion key={d.id} origin="fade" className="flex min-h-8 items-center gap-2 px-2 py-1.5">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-canvas text-ink-muted" aria-hidden>
                <FileText size={14} />
              </span>
              <span className="min-w-0 flex-1 truncate text-ui text-ink" title={d.filename}>
                {d.filename}
              </span>
              <span className="text-meta text-ink-muted">{t("{n} caractères", { n: d.characters })}</span>
              <IconButton size="sm" variant="danger" onClick={() => removeDoc.mutate(d.id)} title={t("Retirer")} aria-label={t("Retirer")}>
                <Trash2 size={14} />
              </IconButton>
            </Motion>
          ))}
          {enemy && docs.length === 0 && (
            <p className="flex items-center gap-2 px-2 py-1.5 text-meta text-ink-muted">
              <FileText size={14} className="shrink-0" aria-hidden /> {t("Aucun document déposé pour l'instant.")}
            </p>
          )}
          {!enemy && <p className="px-2 text-meta text-ink-muted">{t("Enregistrez l'ENI pour déposer des documents.")}</p>}
        </div>
      </PanelGroup>
      <input
        ref={input}
        type="file"
        hidden
        accept=".pdf,.doc,.docx,.odt,.rtf,.txt"
        onChange={(e) => {
          pick(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
    </>
  );
}

// --- Modes d'action -----------------------------------------------------------------------------------------------

function ModesStep({
  enemy,
  edits,
  setEdits,
  dirty,
  generating,
  onGenerate,
  composition,
  composing,
  recomposing,
  onRecompose,
}: {
  enemy: CustomEnemyDetail;
  edits: TextEdits;
  setEdits: (update: (e: TextEdits) => TextEdits) => void;
  dirty: boolean;
  generating: boolean;
  onGenerate: () => void;
  composition: CustomComposition | null;
  composing: boolean;
  /** ME dont une nouvelle proposition est demandée (avant le début du travail). */
  recomposing: string | null;
  onRecompose: (key: string) => void;
}) {
  const [language, setLanguage] = useState<"fr" | "en">(lang() === "en" ? "en" : "fr");
  const [regenerate, setRegenerate] = useState(false);
  const places = new Map((composition?.places ?? []).map((p) => [p.key, p]));
  // Pendant la composition des quatorze, seuls les ME prêts sont montrés : chacun entre en fondu à son arrivée.
  const scenarios = Object.values(enemy.scenarios?.scenarios ?? {}).filter((s) => !(composing && places.get(s.key)?.origin === "pending"));
  const targets = composing && composition?.mode === "one" ? (composition.places.filter((p) => composition.targets.includes(p.place)).map((p) => p.key)) : [];
  const progress = composing && composition?.mode === "all" && <ComposeProgress composition={composition} />;
  if (!scenarios.length && progress) return progress;
  if (!scenarios.length) {
    return (
      <EmptyState
        variant="inline"
        icon={Sparkles}
        title={t("Modes d'action à composer")}
        action={
          <Button onClick={onGenerate} disabled={generating}>
            <WandSparkles size={14} />{" "}
            {generating ? (
              <Waiting>{t("Composition des modes d'action…")}</Waiting>
            ) : dirty ? (
              t("Enregistrer et composer les modes d'action")
            ) : (
              t("Composer les modes d'action")
            )}
          </Button>
        }
      >
        {t("Le modèle compose sept ME1 (les plus probables) et sept ME2 (les plus dangereux) d'après la fiche, en quelques minutes : vous les relisez et les retouchez avant de valider l'ENI.")}
      </EmptyState>
    );
  }
  const families = (["ME1", "ME2"] as const).map((family) => ({
    family,
    items: scenarios.filter((s) => s.family === family).sort((a, b) => b[family === "ME1" ? "mlcoaBias" : "mdcoaBias"] - a[family === "ME1" ? "mlcoaBias" : "mdcoaBias"]),
  }));
  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <Segmented<"fr" | "en">
          ariaLabel={t("Langue des textes")}
          fill={false}
          options={[
            { value: "fr", label: t("Français") },
            { value: "en", label: t("Anglais") },
          ]}
          value={language}
          onChange={setLanguage}
        />
        <span className="flex-1" />
        <Button variant="secondary" size="sm" onClick={() => setRegenerate(true)} disabled={generating || dirty || composing}>
          <WandSparkles size={14} /> {t("Recomposer d'après la fiche")}
        </Button>
      </div>
      {progress}
      <Presence show={!composing && (composition?.state === "stopped" || composition?.state === "failed")}>
        <Motion origin="fade">
          <Callout tone="warn" report={false}>
            {composition?.error ? tServer(composition.error) : t("Composition arrêtée")}
          </Callout>
        </Motion>
      </Presence>
      <p className="text-meta leading-snug text-ink-muted">
        {t("Les repères entre crochets, comme [niveauInitialEni], sont remplacés par les unités et les lieux du dossier.")}
      </p>
      {families.map(({ family, items }) => (
        <PanelGroup
          key={family}
          title={family === "ME1" ? t("ME1 : les plus probables") : t("ME2 : les plus dangereux")}
          count={items.length}
          className="rounded-lg border border-line bg-surface"
        >
          <div className="space-y-3 px-2 pb-2">
            {items.map((s) => (
              <Motion key={s.key} origin="fade">
                <ScenarioEditor
                  enemy={enemy}
                  scenario={s}
                  language={language}
                  edit={edits[s.key]}
                  onEdit={(changes) => setEdits((e) => ({ ...e, [s.key]: { ...e[s.key], ...changes } }))}
                  origin={composition ? places.get(s.key)?.origin : undefined}
                  edited={Boolean(places.get(s.key)?.edited)}
                  locked={composing}
                  dirty={dirty}
                  recomposing={recomposing === s.key || targets.includes(s.key)}
                  onRecompose={() => onRecompose(s.key)}
                />
              </Motion>
            ))}
          </div>
        </PanelGroup>
      ))}
      <Presence show={regenerate}>
        <ConfirmDialog
          title={t("Recomposer les modes d'action ?")}
          tone="neutral"
          confirmLabel={t("Recomposer")}
          busyLabel={t("En cours…")}
          busy={generating}
          onConfirm={() => {
            onGenerate();
            setRegenerate(false);
          }}
          onCancel={() => setRegenerate(false)}
        >
          <p>{t("Les quatorze modes d'action sont composés de nouveau par le modèle d'après la fiche, en quelques minutes : vos retouches sont remplacées.")}</p>
        </ConfirmDialog>
      </Presence>
    </>
  );
}

function ScenarioEditor({
  enemy,
  scenario,
  language,
  edit,
  onEdit,
  origin,
  edited,
  locked,
  dirty,
  recomposing,
  onRecompose,
}: {
  enemy: CustomEnemyDetail;
  scenario: CustomScenario;
  language: "fr" | "en";
  edit: TextEdits[string] | undefined;
  onEdit: (changes: TextEdits[string]) => void;
  /** Origine du ME : composé par le modèle, ou à partir d'un modèle de manœuvre (repli). */
  origin: CustomComposition["places"][number]["origin"] | undefined;
  /** Textes retouchés et enregistrés : une nouvelle proposition demande confirmation. */
  edited: boolean;
  /** Composition en cours : textes en lecture seule. */
  locked: boolean;
  dirty: boolean;
  recomposing: boolean;
  onRecompose: () => void;
}) {
  const notify = useUi((s) => s.notify);
  const [replacing, setReplacing] = useState(false);
  const propose = () => (edited ? setReplacing(true) : onRecompose());
  const nameKey = language === "en" ? "nameEN" : "nameFR";
  const textKey = language === "en" ? "descriptionEN" : "descriptionFR";
  const name = edit?.[nameKey] ?? scenario[nameKey];
  const text = edit?.[textKey] ?? scenario[textKey];
  const rewrite = useMutation({
    mutationFn: () => api.rewriteCustomScenario(enemy.id, scenario.key, language),
    onSuccess: (proposal) => {
      onEdit({ [textKey]: proposal.text });
      notify({
        text: t("Texte reformulé"),
        detail: t("Relisez-le, puis enregistrez pour le garder."),
        tone: "ok",
      });
    },
    onError: (e: Error) => notify({ text: t("Reformulation écartée"), detail: e.message, tone: "warn" }),
  });
  const orientation = t(
    scenario.postureOrientation === "offensive"
      ? "ENI offensif"
      : scenario.postureOrientation === "defensive"
        ? "ENI défensif"
        : "ENI offensif ou défensif",
  );
  return (
    <div className={clsx("space-y-2 rounded-lg border border-line px-3 py-2.5 transition-opacity", recomposing && "opacity-70")}>
      <div className="flex flex-wrap items-center gap-2">
        <input
          className={inputClass}
          readOnly={locked}
          aria-label={t("Titre du mode d'action")}
          value={readableMarkers(name, language)}
          maxLength={160}
          onChange={(e) => onEdit({ [nameKey]: markersFromReadable(e.target.value, language) })}
        />
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge>{orientation}</Badge>
        {origin === "template" && <Badge title={t("Le modèle n'a pas pu proposer ce mode d'action : il reprend un modèle de manœuvre de Generex.")}>{t("Composé à partir d'un modèle de manœuvre")}</Badge>}
        <span className="flex-1" />
        <Button
          variant="link"
          onClick={propose}
          disabled={recomposing || locked}
          blocked={dirty && !locked}
          onBlocked={() => notify({ text: t("Enregistrez d'abord vos retouches"), detail: t("Une nouvelle proposition relit l'ENI enregistré."), tone: "warn" })}
        >
          <Shuffle size={14} /> {recomposing ? <Waiting>{t("Composition d'un autre mode d'action…")}</Waiting> : t("Proposer un autre mode d'action")}
        </Button>
        <Button variant="link" onClick={() => rewrite.mutate()} disabled={rewrite.isPending || locked}>
          <Sparkles size={14} /> {rewrite.isPending ? <Waiting>{t("Reformulation en cours…")}</Waiting> : t("Reformuler avec le modèle de l'ENI")}
        </Button>
      </div>
      <textarea
        className={textareaClass}
        readOnly={locked}
        aria-label={t("Texte du mode d'action")}
        rows={7}
        value={readableMarkers(text, language)}
        onChange={(e) => onEdit({ [textKey]: markersFromReadable(e.target.value, language) })}
      />
      {hasMarkers(text) && (
        <p className="flex items-start gap-1.5 text-meta leading-snug text-ink-muted">
          <MapPin size={12} className="mt-0.5 shrink-0" aria-hidden />
          {t("Les mentions entre ⟨ ⟩ sont remplacées, dans chaque dossier, par les noms de la carte : unités ennemies, positions et localités. Gardez-les telles quelles.")}
        </p>
      )}
      <Presence show={replacing}>
        <ConfirmDialog
          title={t("Remplacer ce mode d'action ?")}
          tone="neutral"
          confirmLabel={t("Proposer un autre mode d'action")}
          onConfirm={() => {
            setReplacing(false);
            onRecompose();
          }}
          onCancel={() => setReplacing(false)}
        >
          <p>{t("Vous avez retouché ce mode d'action : la nouvelle proposition remplacera vos retouches.")}</p>
        </ConfirmDialog>
      </Presence>
    </div>
  );
}

